<script setup lang="ts">
/**
 * A file attached to a chat message, drawn as a chip (#1734): its name, its kind as a person reads it (`PDF`, `Word`)
 * and its size, with a thumbnail when it is given an image source. A long name is cut short on screen and kept whole
 * in its title, so it can still be read.
 *
 * Preview, Open and Download are drawn only when the screen wires them (`@preview`, `@open`, `@download`): a chip in
 * the composer, whose file is not sent yet, has none. Each action is named with the file (`Download brief.pdf`), so
 * a row of chips never reads as a row of identical buttons. `notice` is one plain line under the name, such as that
 * the file is no longer available, drawn in place of anything broken.
 *
 * An unsent file in the composer (#2085) can carry an upload `state` (uploading, failed), drawn in that same notice
 * line, and `@retry` / `@remove` controls (`Retry brief.pdf`, `Remove brief.pdf`). A chip with none of these draws
 * exactly as before.
 *
 * The chip fetches nothing itself: the screen owns where the bytes come from, and hands the thumbnail in.
 */
import { DownloadIcon, ExternalLinkIcon, EyeIcon, FileIcon, RotateCwIcon, XIcon } from '@lucide/vue'
import { computed } from 'vue'
import { Button } from './ui/button'
import { Spinner } from './ui/spinner'

const props = defineProps<{
    /** The file's full name. */
    filename: string
    /** The kind as a person reads it: `PDF`, `PNG`, `Word`. */
    typeLabel: string
    /** The size as a person reads it: `9 bytes`, `1.5 MB`. */
    size: string
    /** A thumbnail image source; only an image has one. */
    thumbnailSrc?: string
    /** One plain line under the name, such as `This file is no longer available.` */
    notice?: string
    /** Wired with `@preview`: shows the file inside the screen. */
    onPreview?: () => void
    /** Wired with `@open`: opens the file in a new tab. */
    onOpen?: () => void
    /** Wired with `@download`: saves the file under its own name. */
    onDownload?: () => void
    /**
     * The upload state of an unsent file (#2085): `uploading` draws a Spinner and `Uploading…`, `failed` says
     * `Couldn't upload this file.` Omitted, the chip is an ordinary file. The state is drawn in the chip's one notice
     * line, ahead of `notice`, so a chip never carries two notice lines.
     */
    state?: 'uploading' | 'failed'
    /** Wired with `@retry`: uploads a failed file again. */
    onRetry?: () => void
    /** Wired with `@remove`: takes an unsent file off the message (the × control). */
    onRemove?: () => void
}>()

const hasActions = computed(() =>
    Boolean(props.onPreview || props.onOpen || props.onDownload || props.onRetry || props.onRemove),
)
const hasNotice = computed(() => Boolean(props.state || props.notice))
</script>

<template>
    <div class="zh-file-chip">
        <img v-if="thumbnailSrc" class="zh-file-chip__thumbnail" :src="thumbnailSrc" alt="" />
        <span v-else class="zh-file-chip__glyph" aria-hidden="true"><FileIcon /></span>
        <span class="zh-file-chip__body">
            <span class="zh-file-chip__name truncate" :title="filename">{{ filename }}</span>
            <span class="zh-file-chip__meta">{{ typeLabel }} · {{ size }}</span>
            <span v-if="hasNotice" class="zh-file-chip__notice">
                <Spinner v-if="state === 'uploading'" label="Uploading…" />
                <span v-else-if="state === 'failed'" class="zh-file-chip__state">Couldn't upload this file.</span>
                <span v-if="notice">{{ notice }}</span>
            </span>
        </span>
        <span v-if="hasActions" class="zh-file-chip__actions">
            <Button
                v-if="onPreview"
                type="button"
                variant="ghost"
                size="icon-sm"
                :aria-label="`Preview ${filename}`"
                title="Preview"
                @click="onPreview()"
            >
                <EyeIcon />
            </Button>
            <Button
                v-if="onOpen"
                type="button"
                variant="ghost"
                size="icon-sm"
                :aria-label="`Open ${filename}`"
                title="Open in a new tab"
                @click="onOpen()"
            >
                <ExternalLinkIcon />
            </Button>
            <Button
                v-if="onDownload"
                type="button"
                variant="ghost"
                size="icon-sm"
                :aria-label="`Download ${filename}`"
                title="Download"
                @click="onDownload()"
            >
                <DownloadIcon />
            </Button>
            <Button
                v-if="onRetry"
                type="button"
                variant="ghost"
                size="icon-sm"
                :aria-label="`Retry ${filename}`"
                title="Retry"
                @click="onRetry()"
            >
                <RotateCwIcon />
            </Button>
            <Button
                v-if="onRemove"
                type="button"
                variant="ghost"
                size="icon-sm"
                :aria-label="`Remove ${filename}`"
                title="Remove"
                @click="onRemove()"
            >
                <XIcon />
            </Button>
        </span>
    </div>
</template>

<style scoped>
.zh-file-chip {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    min-inline-size: 0;
    max-inline-size: 100%;
    padding: 0.375rem 0.5rem;
    border: 1px solid var(--line);
    background: var(--void-black);
    color: var(--ink-1);
}

.zh-file-chip__thumbnail,
.zh-file-chip__glyph {
    flex-shrink: 0;
    inline-size: 2.5rem;
    block-size: 2.5rem;
    border: 1px solid var(--hair);
}

.zh-file-chip__thumbnail {
    object-fit: cover;
}

.zh-file-chip__glyph {
    display: grid;
    place-items: center;
    color: var(--ink-3);
}

.zh-file-chip__glyph :deep(svg) {
    inline-size: 1.125rem;
    block-size: 1.125rem;
}

.zh-file-chip__body {
    display: grid;
    flex: 1;
    gap: 0.125rem;
    min-inline-size: 0;
}

/* Cut short on one line; the title keeps the whole name. */
.zh-file-chip__name {
    overflow: hidden;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow-wrap: normal;
}

/* The system label (BG §19.3): the kind and the size. */
.zh-file-chip__meta {
    color: var(--ink-3);
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    font-weight: 500;
    letter-spacing: var(--tracking-system);
    line-height: 1.4;
    text-transform: uppercase;
}

.zh-file-chip__notice {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem 0.375rem;
    color: var(--ink-2);
    font-size: 0.75rem;
    line-height: 1.4;
}

/* A failed upload reads as words first; the weight marks it without leaning on colour. */
.zh-file-chip__state {
    color: var(--ink-1);
    font-weight: 600;
}

.zh-file-chip__actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 0.125rem;
}
</style>
